import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/base/SectionHeading";
import { getRelatedFaqs, ISSUE_TYPE_CATEGORY } from "@/pages/help/faqData";
import { resolveFaqView, useFaqOverrides } from "@/pages/help/faqContent";
import {
  readFaqFeedback,
  readRecentFaqKeys,
  writeFaqFeedback,
  type FaqFeedback,
} from "@/pages/help/helpStorage";
import { recordFaqFeedback } from "@/pages/help/faqFeedback";

const FORM_ACTION = "https://readdy.ai/api/form/db1lpgeoc082o2i1kcrg";

const ISSUE_TYPES = [
  "helpCenter.formTypeAccount",
  "helpCenter.formTypeApply",
  "helpCenter.formTypeLicense",
  "helpCenter.formTypeRenewal",
  "helpCenter.formTypeIntegration",
  "helpCenter.formTypeOther",
];

const SUPPORT_POINTS = [
  { icon: "ri-timer-flash-line", titleKey: "helpCenter.contactPoint1Title", descKey: "helpCenter.contactPoint1Desc" },
  { icon: "ri-customer-service-2-line", titleKey: "helpCenter.contactPoint2Title", descKey: "helpCenter.contactPoint2Desc" },
  { icon: "ri-book-read-line", titleKey: "helpCenter.contactPoint3Title", descKey: "helpCenter.contactPoint3Desc" },
];

interface SupportTicketProps {
  prefillIssueType?: string;
  prefillNonce?: number;
}

export default function SupportTicket({ prefillIssueType = "", prefillNonce = 0 }: SupportTicketProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [messageLength, setMessageLength] = useState(0);
  const [issueType, setIssueType] = useState("");
  const [openSuggest, setOpenSuggest] = useState<string[]>([]);
  const [recentKeys, setRecentKeys] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<Record<string, FaqFeedback>>(readFaqFeedback);
  const { overrides } = useFaqOverrides();

  useEffect(() => {
    writeFaqFeedback(feedback);
  }, [feedback]);

  // A "not helpful" feedback from the FAQ jumps here and pre-fills the issue type.
  useEffect(() => {
    if (!prefillIssueType) return;
    setIssueType(prefillIssueType);
    setFieldErrors((prev) => {
      if (!prev.issue_type) return prev;
      const next = { ...prev };
      delete next.issue_type;
      return next;
    });
  }, [prefillIssueType, prefillNonce]);

  useEffect(() => {
    setOpenSuggest([]);
    if (issueType) setRecentKeys(readRecentFaqKeys());
  }, [issueType]);

  const relatedFaqs = useMemo(() => {
    const category = ISSUE_TYPE_CATEGORY[issueType];
    return getRelatedFaqs(category, 3, recentKeys).map((item) =>
      resolveFaqView(item, overrides[item.qKey], t),
    );
  }, [issueType, recentKeys, overrides, t]);

  const toggleSuggest = (qKey: string) => {
    setOpenSuggest((prev) => (prev.includes(qKey) ? prev.filter((k) => k !== qKey) : [...prev, qKey]));
  };

  // Self-service closes the loop right inside the form: read the answer, then
  // mark it helpful / not helpful without leaving the ticket flow.
  const markSuggestFeedback = (faq: { qKey: string; cat: string }, value: FaqFeedback) => {
    setFeedback((prev) => ({ ...prev, [faq.qKey]: value }));
    void recordFaqFeedback(faq.qKey, faq.cat, value);
  };

  const inputClass = "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      form.reset();
      setMessageLength(0);
      setIssueType("");
      setFieldErrors({});
      setStatus("success");
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const issueType = String(formData.get("issue_type") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    const errors: Record<string, string> = {};
    if (!name) errors.name = t("helpCenter.formErrName");
    if (!email) errors.email = t("helpCenter.formErrEmail");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = t("helpCenter.formErrEmailFormat");
    if (!issueType) errors.issue_type = t("helpCenter.formErrType");
    if (!message) errors.message = t("helpCenter.formErrContent");
    else if (message.length > 500) errors.message = t("helpCenter.formErrContentMax");

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError("");
      setStatus("idle");
      return;
    }

    setFieldErrors({});
    setFormError("");
    setStatus("submitting");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const normalized = String(value).trim();
      if (normalized) payload.append(key, normalized);
    });

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || "";
      const success = response.ok && parsed?.code === "OK";
      if (success) {
        form.reset();
        setMessageLength(0);
        setIssueType("");
        setStatus("success");
      } else {
        setStatus("error");
        setFormError(serverMsg || t("helpCenter.formErrFailed"));
      }
    } catch {
      setStatus("error");
      setFormError(t("helpCenter.formErrFailed"));
    }
  };

  return (
    <section id="contact" className="scroll-mt-20 border-t border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-5xl px-4 py-14 md:px-6 md:py-16">
        <SectionHeading
          eyebrow={t("helpCenter.contactEyebrow")}
          title={t("helpCenter.contactTitle")}
          description={t("helpCenter.contactDesc")}
        />

        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_1.5fr]">
          <div className="space-y-3">
            {SUPPORT_POINTS.map((point) => (
              <div
                key={point.titleKey}
                className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${point.icon} text-lg`} />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">{t(point.titleKey)}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-foreground-600">{t(point.descKey)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            {status === "success" ? (
              <div className="flex flex-col items-center py-10 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-checkbox-circle-line text-3xl" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                  {t("helpCenter.formSuccessTitle")}
                </h3>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                  {t("helpCenter.formSuccessDesc")}
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-5 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-add-line" />
                  </span>
                  {t("helpCenter.formAnother")}
                </button>
              </div>
            ) : (
              <form id="help-support-ticket" data-readdy-form onSubmit={handleSubmit} noValidate className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="ticket-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      {t("helpCenter.formName")}
                    </label>
                    <input
                      id="ticket-name"
                      name="name"
                      type="text"
                      placeholder={t("helpCenter.formNamePlaceholder")}
                      className={inputClass}
                    />
                    {fieldErrors.name && <p className="mt-1.5 text-xs text-accent-700">{fieldErrors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="ticket-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      {t("helpCenter.formEmail")}
                    </label>
                    <input
                      id="ticket-email"
                      name="email"
                      type="email"
                      placeholder={t("helpCenter.formEmailPlaceholder")}
                      className={inputClass}
                    />
                    {fieldErrors.email && <p className="mt-1.5 text-xs text-accent-700">{fieldErrors.email}</p>}
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="ticket-company" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      {t("helpCenter.formCompany")}
                    </label>
                    <input
                      id="ticket-company"
                      name="company"
                      type="text"
                      placeholder={t("helpCenter.formCompanyPlaceholder")}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="ticket-type" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      {t("helpCenter.formType")}
                    </label>
                    <select
                      id="ticket-type"
                      name="issue_type"
                      value={issueType}
                      className={`${inputClass} cursor-pointer`}
                      onChange={(e) => {
                        setIssueType(e.target.value);
                        setFieldErrors((prev) => {
                          if (!prev.issue_type) return prev;
                          const next = { ...prev };
                          delete next.issue_type;
                          return next;
                        });
                      }}
                    >
                      <option value="" disabled>
                        {t("helpCenter.formTypePlaceholder")}
                      </option>
                      {ISSUE_TYPES.map((key) => (
                        <option key={key} value={key}>
                          {t(key)}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.issue_type && <p className="mt-1.5 text-xs text-accent-700">{fieldErrors.issue_type}</p>}
                  </div>
                </div>

                {relatedFaqs.length > 0 && (
                  <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-4">
                    <div className="flex items-start gap-2.5">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center text-secondary-700">
                        <i className="ri-lightbulb-flash-line" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground-950">
                          {t("helpCenter.formSuggestTitle")}
                        </p>
                        <p className="mt-0.5 text-xs leading-relaxed text-foreground-600">
                          {t("helpCenter.formSuggestHint")}
                        </p>
                        <div className="mt-3 overflow-hidden rounded-md border border-background-200 bg-background-50">
                          {relatedFaqs.map((faq) => {
                            const open = openSuggest.includes(faq.qKey);
                            const isRecent = recentKeys.includes(faq.qKey);
                            const fb = feedback[faq.qKey];
                            return (
                              <div key={faq.qKey} className="border-b border-background-200 last:border-b-0">
                                <button
                                  type="button"
                                  onClick={() => toggleSuggest(faq.qKey)}
                                  aria-expanded={open}
                                  className="flex w-full cursor-pointer items-center justify-between gap-3 px-3.5 py-2.5 text-left transition-colors hover:bg-background-100"
                                >
                                  <span className="flex min-w-0 items-center gap-2">
                                    {isRecent && (
                                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-medium text-secondary-900">
                                        <i className="ri-history-line" />
                                        {t("helpCenter.faqRecentBadge")}
                                      </span>
                                    )}
                                    <span className="text-sm text-foreground-900">{faq.question}</span>
                                  </span>
                                  <span className="flex h-4 w-4 shrink-0 items-center justify-center text-foreground-500">
                                    <i className={open ? "ri-subtract-line" : "ri-add-line"} />
                                  </span>
                                </button>
                                {open && (
                                  <div className="border-t border-background-200 px-3.5 py-3">
                                    <p className="text-xs leading-relaxed text-foreground-600">{faq.answer}</p>
                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                      <span className="text-[11px] text-foreground-500">
                                        {t("helpCenter.faqHelpfulQuestion")}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => markSuggestFeedback(faq, "helpful")}
                                        className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border px-2.5 py-1 text-[11px] transition-colors ${
                                          fb === "helpful"
                                            ? "border-primary-300 bg-primary-100 text-primary-800"
                                            : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                                        }`}
                                      >
                                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                                          <i className="ri-thumb-up-line" />
                                        </span>
                                        {t("helpCenter.faqHelpful")}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => markSuggestFeedback(faq, "notHelpful")}
                                        className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border px-2.5 py-1 text-[11px] transition-colors ${
                                          fb === "notHelpful"
                                            ? "border-secondary-300 bg-secondary-100 text-secondary-900"
                                            : "border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-300"
                                        }`}
                                      >
                                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                                          <i className="ri-thumb-down-line" />
                                        </span>
                                        {t("helpCenter.faqNotHelpful")}
                                      </button>
                                      {fb && (
                                        <span className="text-[11px] text-foreground-500">
                                          {t("helpCenter.faqFeedbackThanks")}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <Link
                          to="/help#faq"
                          className="mt-3 inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-primary-700 transition-colors hover:text-primary-800"
                        >
                          {t("helpCenter.formSuggestMore")}
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-arrow-right-line" />
                          </span>
                        </Link>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="ticket-license" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    {t("helpCenter.formLicense")}
                  </label>
                  <input
                    id="ticket-license"
                    name="license_key"
                    type="text"
                    placeholder={t("helpCenter.formLicensePlaceholder")}
                    className={`${inputClass} font-mono`}
                  />
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label htmlFor="ticket-content" className="block text-sm font-medium text-foreground-800">
                      {t("helpCenter.formContent")}
                    </label>
                    <span className={`text-xs ${messageLength > 500 ? "text-accent-700" : "text-foreground-400"}`}>
                      {messageLength}/500
                    </span>
                  </div>
                  <textarea
                    id="ticket-content"
                    name="message"
                    rows={5}
                    maxLength={500}
                    onChange={(event) => setMessageLength(event.target.value.length)}
                    placeholder={t("helpCenter.formContentPlaceholder")}
                    className={`${inputClass} resize-none`}
                  />
                  {fieldErrors.message && <p className="mt-1.5 text-xs text-accent-700">{fieldErrors.message}</p>}
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="aux-field-slot"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && formError && (
                  <div className="flex items-start gap-2.5 rounded-md border border-accent-300 bg-accent-50 px-4 py-3">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center text-accent-700">
                      <i className="ri-error-warning-line" />
                    </span>
                    <p className="text-sm text-accent-700">{formError}</p>
                  </div>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-foreground-500">{t("helpCenter.formNote")}</p>
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === "submitting" ? (
                      <span className="flex h-4 w-4 items-center justify-center animate-spin">
                        <i className="ri-loader-4-line" />
                      </span>
                    ) : (
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-send-plane-line" />
                      </span>
                    )}
                    {status === "submitting" ? t("helpCenter.formSubmitting") : t("helpCenter.formSubmit")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}